import React, { useEffect, useMemo } from 'react';

import { useAISmartButtonActions } from '@atlassian/ai-smart-button-api/ai-smart-button-store';
import { useDailyBriefUnread } from '@atlassian/ai-smart-button-api/state';
import { type ConversationAction } from '@atlassian/rovo-conversation-actions';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { AutomationNudgeActionsLoader } from '@confluence/automation-discovery/entry-points/AutomationNudgeActionsLoader.ts';
import { CommentCountsProvider } from '@confluence/comment-context/entry-points/CommentCountsContext.ts';
import type {
	PageRovoConfigProviderProps,
	RovoConfiguration,
	RovoConfigurationProviderProps,
} from '@confluence/rovo-config-provider-common/entry-points/rovoConfigTypes.ts';
import { useViewPageRovoActions } from '@confluence/page-rovo-config-common/entry-points/useViewPageRovoActions.ts';
import { useRovoConfigProviderProps } from '@confluence/rovo-config-provider-common/entry-points/useRovoConfigProviderProps.ts';
import {
	type PageRovoContributorsRef,
	NudgeContributorFilter,
} from '@confluence/page-rovo-config-common/entry-points/NudgeContributorFilter.ts';
import type { RovoActivationFragmentRef } from '@confluence/page-rovo-config-common/entry-points/RovoActivation.ts';
import { useNudgeFirstVisitFilter } from '@confluence/page-rovo-config-common/entry-points/useNudgeFirstVisitFilter.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useAgentMentionSidebarListener } from '@confluence/agent-mention/entry-points/useAgentMentionSidebarListener.ts';

import { useNudgeRecommendationFilter } from './hooks/useNudgeRecommendationFilter.tsx';
import { useViewPageNudgeConfig } from './hooks/useViewPageNudgeConfig.ts';

const AgentMentionSidebarListener = () => {
	useAgentMentionSidebarListener();
	return null;
};

const ViewPageConfigProviderInner = (props: RovoConfigurationProviderProps) => {
	const { contentType, contentId, contributorsFragmentRef } =
		useRovoConfigProviderProps() as PageRovoConfigProviderProps<
			PageRovoContributorsRef,
			RovoActivationFragmentRef
		>;
	const { isLoggedIn, isLicensed, cloudId } = useSessionData();
	const isSessionReady = isLoggedIn && isLicensed && !!cloudId;
	const { isBaseEligible, readTimeMinutes } = useNudgeRecommendationFilter(contentType, contentId);
	const skipContributorFilter =
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		!isBaseEligible || !isSessionReady || !fg('aix_convo_starter_nudge_enabled');
	return (
		<NudgeContributorFilter
			contentId={contentId}
			skip={skipContributorFilter}
			contributorsFragmentRef={contributorsFragmentRef}
		>
			{(isCurrentUserContributor: boolean) => (
				<ViewPageConfigProviderWithContributorStatus
					{...props}
					isBaseEligible={isBaseEligible}
					readTimeMinutes={readTimeMinutes}
					isCurrentUserContributor={isCurrentUserContributor}
					skipContributorFilter={skipContributorFilter}
					isSessionReady={isSessionReady}
				/>
			)}
		</NudgeContributorFilter>
	);
};

const ViewPageConfigProviderWithContributorStatus = ({
	setConfiguration,
	isBaseEligible,
	readTimeMinutes,
	isCurrentUserContributor,
	skipContributorFilter,
	isSessionReady,
}: RovoConfigurationProviderProps & {
	isBaseEligible: boolean;
	readTimeMinutes: number | null;
	isCurrentUserContributor: boolean;
	skipContributorFilter: boolean;
	isSessionReady: boolean;
}) => {
	const { rovoActivationFragmentRef, contentId, contentType } =
		useRovoConfigProviderProps() as PageRovoConfigProviderProps<
			PageRovoContributorsRef,
			RovoActivationFragmentRef
		>;
	const viewPageRovoActions = useViewPageRovoActions();
	const { isFirstVisit } = useNudgeFirstVisitFilter(contentId, skipContributorFilter);
	const dailyBriefStatus = useDailyBriefUnread();
	const { nudges, errors: nudgeErrors } = useViewPageNudgeConfig({
		rovoActivationFragmentRef,
		contentId,
		contentType,
		isBaseEligible,
		readTimeMinutes,
		isCurrentUserContributor,
		isFirstVisit,
		dailyBriefStatus,
		isSessionReady,
	});
	const { setNudges } = useAISmartButtonActions();

	const rovoConfiguration = useMemo<RovoConfiguration>(() => {
		return {
			getActions: async () => {
				const actions = viewPageRovoActions.map(
					(action: { action: ConversationAction }) => action.action,
				);
				return Promise.resolve(actions);
			},
		};
	}, [viewPageRovoActions]);

	useEffect(() => {
		setConfiguration(rovoConfiguration);
		setNudges(nudges);
	}, [rovoConfiguration, setConfiguration, nudges, setNudges]);

	return (
		<>
			{nudgeErrors.map((error: Error, index: number) => (
				<ErrorDisplay key={`${error.name}-${index}`} error={error} />
			))}
			{/* eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred. */}
			{fg('cc_automations_nudge_actions_error_boundary') ? (
				<AutomationNudgeActionsLoader contentId={contentId} />
			) : null}
			{expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true) && (
				<AgentMentionSidebarListener />
			)}
		</>
	);
};

export const ViewPageRovoConfigProvider = (
	props: RovoConfigurationProviderProps,
): React.JSX.Element => {
	const { contentId } = useRovoConfigProviderProps() as PageRovoConfigProviderProps<
		PageRovoContributorsRef,
		RovoActivationFragmentRef
	>;
	return (
		<CommentCountsProvider contentId={contentId}>
			<ViewPageConfigProviderInner {...props} />
		</CommentCountsProvider>
	);
};
